import { Badge, Box, Button, Group, LoadingOverlay, NativeSelect, Paper, Stack, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconArrowLeft, IconSearch } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { dateToEndOfDay, dateToStartOfDay } from '../../helpers/dateUtils';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import { ScreenHint } from '../KnowledgeBase/shared';
import Spinner from '../Spinner';
import ConversationDetail, { ConversationPlaceholder } from './ConversationDetail';
import ConversationList from './ConversationList';
import { ANSWER_TYPE_FILTERS, VOTE_FILTERS } from './conversationFormat';
import classes from './Conversations.module.css';

const SEARCH_DEBOUNCE_MS = 400;
export const CONVERSATIONS_PAGE_SIZE = 25;

let requestSeq = 0;

const newRequest = (filterKey, endDate) => {
  const endOfRange = dateToEndOfDay(endDate, 'local');
  const now = new Date();
  return {
    filterKey,
    seq: ++requestSeq,
    page: 1,
    retry: 0,
    to: new Date(endOfRange) > now ? now.toISOString() : endOfRange,
  };
};

const ConversationPane = ({ fetchUrl, sessionId, accountId, domainId, onBack }) => {
  const fetch = useFetch();
  const [loaded, setLoaded] = useState({ sessionId: null, session: null, error: null });

  useEffect(() => {
    let cancelled = false;
    fetch(`${fetchUrl}/chatBot/sessions/${encodeURIComponent(sessionId)}`)
      .then(({ session }) => {
        if (!cancelled) setLoaded({ sessionId, session, error: null });
      })
      .catch((error) => {
        if (!cancelled) setLoaded({ sessionId, session: null, error });
      });
    return () => {
      cancelled = true;
    };
  }, [fetch, fetchUrl, sessionId]);

  if (loaded.sessionId !== sessionId) return <Spinner />;
  if (loaded.error || !loaded.session) {
    return (
      <Stack align="flex-start" p="md" gap="sm">
        <Button
          variant="subtle"
          className={classes.mobileOnly}
          leftSection={<IconArrowLeft size={16} />}
          onClick={onBack}
        >
          All conversations
        </Button>
        <ErrorMessage message={loaded.error?.message || 'Could not load this conversation.'} />
      </Stack>
    );
  }

  return (
    <ConversationDetail
      session={loaded.session}
      accountId={accountId}
      domainId={domainId}
      onBack={onBack}
      fetchUrl={fetchUrl}
    />
  );
};

const ConversationsTab = ({ fetchUrl, accountId, domainId, startDate, endDate, sessionId, onSessionChange }) => {
  const fetch = useFetch();

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);
  const [answerType, setAnswerType] = useState('');
  const [vote, setVote] = useState('');
  const filterKey = JSON.stringify([startDate, endDate, debouncedSearch, answerType, vote]);

  const [requested, setRequested] = useState(() => newRequest(filterKey, endDate));
  if (requested.filterKey !== filterKey) setRequested(newRequest(filterKey, endDate));
  const { seq, page, retry, to } = requested;
  const requestKey = `${seq}|${page}|${retry}`;

  const [list, setList] = useState({ seq: null, page: 0, sessions: [], total: 0, exhausted: false });
  const [listError, setListError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams({
      page: String(page),
      perPage: String(CONVERSATIONS_PAGE_SIZE),
      from: dateToStartOfDay(startDate, 'local'),
      to,
    });
    if (debouncedSearch) params.set('q', debouncedSearch);
    if (answerType) params.set('answerType', answerType);
    if (vote) params.set('vote', vote);

    fetch(`${fetchUrl}/chatBot/sessions?${params}`)
      .then((response) => {
        if (cancelled) return;
        setList((previous) => {
          const kept = page > 1 && previous.seq === seq ? previous.sessions : [];
          const seen = new Set(kept.map((session) => session.chatSessionId));
          const received = response.sessions || [];
          const added = received.filter((session) => !seen.has(session.chatSessionId));
          const exhausted = page > 1 && (added.length === 0 || received.length < CONVERSATIONS_PAGE_SIZE);
          return { seq, page, sessions: [...kept, ...added], total: response.total || 0, exhausted };
        });
      })
      .catch((fetchError) => {
        if (!cancelled) setListError({ key: requestKey, error: fetchError });
      });
    return () => {
      cancelled = true;
    };
  }, [fetch, fetchUrl, startDate, to, debouncedSearch, answerType, vote, page, seq, requestKey]);

  const error = listError?.key === requestKey ? listError.error : null;
  const firstLoad = list.seq === null;
  const refreshing = !firstLoad && list.seq !== seq && !error;
  const showList = !firstLoad && !(error && list.seq !== seq);
  const loadingMore = list.seq === seq && list.page < page;

  return (
    <Box className={classes.layout} data-has-selection={sessionId ? 'true' : undefined} mt="lg">
      <Paper withBorder radius="md" className={`${classes.pane} ${classes.listPane}`}>
        <Box className={classes.paneHeader}>
          <Group justify="space-between" mb="xs" wrap="nowrap">
            <Group gap={6}>
              <Text fw={600}>Conversations</Text>
              {!firstLoad && (
                <Badge variant="light" size="sm">
                  {list.total}
                </Badge>
              )}
            </Group>
            <ScreenHint text="Every conversation shoppers had with the sales agent in the selected period, newest first. Use Export above to receive all messages of the period as a CSV." />
          </Group>
          <Group gap="xs" wrap="nowrap">
            <TextInput
              placeholder="Search questions"
              aria-label="Search shopper questions"
              leftSection={<IconSearch size={14} />}
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
              maxLength={200}
              style={{ flex: 1 }}
            />
            <NativeSelect
              aria-label="Answer type"
              data={ANSWER_TYPE_FILTERS}
              value={answerType}
              onChange={(event) => setAnswerType(event.currentTarget.value)}
              w={130}
            />
            <NativeSelect
              aria-label="Votes"
              data={VOTE_FILTERS}
              value={vote}
              onChange={(event) => setVote(event.currentTarget.value)}
              w={110}
            />
          </Group>
        </Box>
        <Box className={classes.paneBody} pos="relative">
          <LoadingOverlay visible={refreshing} overlayProps={{ blur: 1 }} loaderProps={{ size: 'sm' }} />
          {firstLoad && !error && <Spinner />}
          {showList && (
            <ConversationList
              sessions={list.sessions}
              total={list.total}
              selectedId={sessionId}
              onOpen={onSessionChange}
              filtered={Boolean(debouncedSearch || answerType || vote)}
              hasMore={list.seq === seq && !list.exhausted && !error && list.sessions.length < list.total}
              onLoadMore={() => setRequested({ ...requested, page: page + 1 })}
              loadingMore={loadingMore}
            />
          )}
          {error && (
            <Stack align="flex-start" gap="xs" p="md">
              <ErrorMessage message={error.message || 'Could not load conversations.'} />
              <Button size="xs" variant="light" onClick={() => setRequested({ ...requested, retry: retry + 1 })}>
                Try again
              </Button>
            </Stack>
          )}
        </Box>
      </Paper>

      <Paper withBorder radius="md" className={`${classes.pane} ${classes.chatPane}`}>
        {sessionId ? (
          <ConversationPane
            key={sessionId}
            fetchUrl={fetchUrl}
            sessionId={sessionId}
            accountId={accountId}
            domainId={domainId}
            onBack={() => onSessionChange(null)}
          />
        ) : (
          <ConversationPlaceholder />
        )}
      </Paper>
    </Box>
  );
};

export default ConversationsTab;
